import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/Button';
import { AlertCircle, CheckCircle2, Clock, Loader2, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { parseJwt, isAllowedSsoEmail, TRUSTYBYTES_EMAIL_DOMAINS } from '../../utils/sso';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithSso } = useAuthStore();
  const [status, setStatus] = useState<'processing' | 'success' | 'pending' | 'error'>('processing');
  const [pendingInfo, setPendingInfo] = useState<{ email?: string; message?: string }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      // 1. Check for error parameters from OAuth provider
      const errorParam = searchParams.get('error');
      const errorDesc = searchParams.get('error_description') || searchParams.get('message');
      if (errorParam) {
        setStatus('error');
        setErrorMessage(errorDesc || `Authentication failed: ${errorParam}`);
        toast.error(errorDesc || 'SSO authentication was cancelled or failed.');
        return;
      }

      // 2. Extract OAuth tokens and user info
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token') || undefined;
      const email = searchParams.get('email') || undefined;
      const name = searchParams.get('name') || undefined;
      const returnedState = searchParams.get('state');

      if (!accessToken) {
        setStatus('error');
        setErrorMessage('No access token received from Central Auth Service.');
        return;
      }

      // Fast user-facing guard: only @trustybytes.in accounts may sign in.
      if (email && !isAllowedSsoEmail(email)) {
        setStatus('error');
        setErrorMessage(
          `Sign in is restricted to ${TRUSTYBYTES_EMAIL_DOMAINS.join(', ')} accounts. ` +
          `Please use your organization Google account (you tried ${email}).`
        );
        toast.error('This Google account is not part of your organization.');
        return;
      }

      // 3. CSRF State validation & token verification
      const savedState = sessionStorage.getItem('sso_csrf_state');
      sessionStorage.removeItem('sso_csrf_state');

      const parsed = parseJwt(accessToken);
      const isValidIssuer = parsed && (parsed.iss?.includes('trustybytes.com') || parsed.clientId?.startsWith('TB_CL_'));

      if (savedState && returnedState && returnedState !== savedState && !isValidIssuer) {
        setStatus('error');
        setErrorMessage('CSRF State mismatch and unverified token origin. For security reasons, authentication was aborted.');
        return;
      }

      // 4. Complete login in AuthStore
      try {
        const result = await loginWithSso({
          accessToken,
          refreshToken,
          email,
          name,
        });

        if (result?.status === 'pending_approval') {
          setStatus('pending');
          setPendingInfo({
            email,
            message: result.message || 'Your access request has been sent to the organization administrator for approval.',
          });
          return;
        }

        setStatus('success');
        toast.success(`Welcome to TeamOne, ${name || email || 'User'}!`);

        // 5. Navigate to intended page or dashboard
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err: any) {
        const errorCode = err?.response?.data?.code;
        const backendMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message;

        if (errorCode === 'ACCOUNT_PENDING_APPROVAL') {
          setStatus('pending');
          setPendingInfo({
            email,
            message: backendMsg || 'Your access request is pending administrator approval.',
          });
          return;
        }

        setStatus('error');
        setErrorMessage(
          typeof backendMsg === 'string' && backendMsg.trim()
            ? backendMsg
            : 'Failed to initialize user session. Please try again.'
        );
      }
    };

    processCallback();
  }, [searchParams, loginWithSso, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md p-8 bg-card border border-border rounded-lg shadow-lg text-center space-y-6">
        <div className="flex justify-center">
          <div className="size-14 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <Shield className="size-7" />
          </div>
        </div>

        {status === 'processing' && (
          <div className="space-y-3">
            <Loader2 className="size-8 animate-spin mx-auto text-primary" />
            <h2 className="text-foreground font-display text-section-title">
              Authenticating with Central SSO
            </h2>
            <p className="text-sm text-muted-foreground">
              Validating token and syncing permissions...
            </p>
          </div>
        )}

        {status === 'pending' && (
          <div className="space-y-4">
            <div className="size-12 rounded-full bg-warning/10 text-warning flex items-center justify-center mx-auto border border-warning/20">
              <Clock className="size-6" />
            </div>
            <div className="space-y-1.5">
              <h2 className="text-foreground font-display text-section-title">
                Access Request Submitted
              </h2>
              <p className="text-xs text-muted-foreground font-mono">
                {pendingInfo.email}
              </p>
            </div>
            <div className="p-3.5 bg-secondary/60 border border-border rounded-lg text-xs text-muted-foreground text-left leading-relaxed space-y-2">
              <p className="font-medium text-foreground">
                Administrator Approval Required
              </p>
              <p>
                {pendingInfo.message}
              </p>
              <p className="text-xs text-muted-foreground pt-1 border-t border-border/50">
                An administrator will review your account, assign your organization role and onboarding task, and activate your access.
              </p>
            </div>
            <div className="pt-2">
              <Button asChild fullWidth variant="primary">
                <Link to="/login">Back to Sign In</Link>
              </Button>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-3">
            <CheckCircle2 className="size-8 text-success mx-auto" />
            <h2 className="text-foreground font-display text-section-title">
              Authentication Successful
            </h2>
            <p className="text-sm text-muted-foreground">
              Redirecting you to your workspace...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="size-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
              <AlertCircle className="size-6" />
            </div>
            <h2 className="text-foreground font-display text-section-title">
              Authentication Failed
            </h2>
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-xs text-destructive text-left leading-relaxed">
              {errorMessage}
            </div>
            <div className="pt-2">
              <Button asChild fullWidth variant="primary">
                <Link to="/login">Back to Sign In</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
